import React, {
  useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, CircularProgress, Divider, LinearProgress, Paper, Stack, TextField, Typography,
} from '@mui/material';
import FileUploadOutlineIcon from 'mdi-react/FileUploadOutlineIcon';
import FloorPlanIcon from 'mdi-react/FloorPlanIcon';
import ImageSearchOutlineIcon from 'mdi-react/ImageSearchOutlineIcon';
import { editorTheme, sectionHeadingSx } from '../utils/editor-theme';
import DrawingImport from '../../dashboard/components/drawing-import.component';
import { EDITOR_FEATURES } from '../utils/editor-features';
import type { DrawingConversion } from '../../dashboard/services/drawing-import.service';
import saveFloorPlanForOffice from '../../dashboard/services/floor-plan.service';
import FloorPlan from '../../dashboard/models/floor-plan';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import type GeoJson from '../../dashboard/models/geo-json';
import type GeoJsonWorkplaces from '../../dashboard/models/geo-json-workplaces';
import type GeoJsonLabels from '../../dashboard/models/geo-json-labels';
import { SAMPLE_UNITS_PER_METRE, sampleFloorPlan } from '../utils/sample-floorplan';
import { serializeFloorPlan } from '../utils/serialize-floorplan';
import { firstFreeStorey, storeyProblemKey, storeyProblemOf } from '../utils/storey-validation';
import notify from '../../../core/components/notify/notify.component';
import {
  PictureUnreadable, TRACE_CANCELLED, asDrawnInBackground, readPictureInBackground, readingStepKeyOf, type ReadingProgress,
} from '../utils/image-file';
import { PICTURE_ACCEPT, unreadableKeyOf } from '../utils/picture-format';
import { IMAGE_PLAN_UNITS_PER_METRE, sketchToFloorPlan } from '../utils/sketch-to-floorplan';
import type { FloorPlanSketch, SketchInk } from '../models/floorplan-sketch';
import ImageReview from './image-review.component';

/** The five stored layers, named as the database names them. */
type LayerKey = 'borderLayer' | 'floorLayer' | 'roomLayer' | 'workplacesLayer' | 'labelLayer';

const LAYERS: ReadonlyArray<{ readonly key: LayerKey; readonly labelKey: string; readonly optional?: boolean }> = [
  { key: 'borderLayer', labelKey: 'floorplan-editor-layer-border' },
  { key: 'floorLayer', labelKey: 'floorplan-editor-layer-floor' },
  { key: 'roomLayer', labelKey: 'floorplan-editor-layer-rooms' },
  { key: 'workplacesLayer', labelKey: 'floorplan-editor-layer-desks' },
  { key: 'labelLayer', labelKey: 'floorplan-editor-layer-labels', optional: true },
];

/** An image being checked: what was read off it, and how it was turned to read it. */
interface ImageReviewState {
  readonly sketch: FloorPlanSketch;
  /** The drawing's own ink: whatever the review does not mark is kept from it as drawn. */
  readonly ink: SketchInk | null;
  readonly imageUrl: string;
  readonly aspect: number;
  /** The file itself, read again when it is turned. */
  readonly file: Blob;
  /** Degrees clockwise the picture was turned before it was read. */
  readonly turn: number;
}

interface Props {
  readonly officeId: string;
  /** Re-reads the office's floorplans, so the editor opens on what was just created — on its storey. */
  readonly onCreated: (storey: number) => void;
  /** The storey to start on, when the editor already knows which floor this is for. */
  readonly initialStorey?: number;
  /** Floors the office already has a plan for, which a new one cannot take. */
  readonly existingStoreys?: readonly number[];
  /** Back to the plans the office already has, when it has any. */
  readonly onCancel?: () => void;
}

/**
 * The first floorplan for an office that has none.
 *
 * Opening the editor on an office with no plan used to be a dead end: an information banner
 * saying there was no floorplan, and no way to make one from the page an administrator had
 * just navigated to in order to make one. The upload lived on a menu item on a different
 * screen, which is a reasonable place for it and a hopeless place to find it.
 *
 * A sample floor to start drawing from is the first of them: somebody setting up a small office
 * usually has nothing to upload, and a floor that already has a border, rooms, desks and labels
 * on it shows what each thing looks like and only needs reshaping.
 *
 * The file routes differ from each other because the two kinds of source are genuinely different. A CAD drawing is one
 * file holding every layer, and its layers have to be mapped by hand — nothing can tell that
 * `01-buitenwanden` is the building outline without guessing, and a guess imports a floor's
 * furniture as its walls. GeoJSON arrives already separated into the five stored layers,
 * usually exported from this tool or another one, so it is uploaded a layer at a time.
 */
const FloorPlanStarter = ({
  officeId, onCreated, initialStorey, onCancel, existingStoreys = [],
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();

  // Kept as typed, so a half-typed "-" or an empty field can be told apart from floor 0.
  const [storeyText, setStoreyText] = useState(String(initialStorey ?? firstFreeStorey(existingStoreys)));
  const storeyProblem = storeyProblemOf(storeyText, existingStoreys);
  const storey = Number(storeyText.trim());
  const [layers, setLayers] = useState<Partial<Record<LayerKey, unknown>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [readingImage, setReadingImage] = useState(false);
  // How far the reading has got, while it runs.
  const [progress, setProgress] = useState<ReadingProgress | null>(null);
  // The building's real width, when the administrator knows it: the one sure way to a true scale.
  const [buildingWidthText, setBuildingWidthText] = useState('');
  const buildingWidth = buildingWidthText.trim() === '' ? null : Number(buildingWidthText.trim());
  const buildingWidthInvalid = buildingWidth !== null && !(Number.isFinite(buildingWidth) && buildingWidth > 0);
  // What was read off an image, waiting for the administrator to check it.
  const [review, setReview] = useState<ImageReviewState | null>(null);
  // The image being read, so reading it can be stopped: when the page is left, or another image chosen.
  const tracing = useRef<AbortController | null>(null);
  // Whether the panel is still showing: a reading that ends after it has gone changes nothing.
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return (): void => {
      mounted.current = false;
      tracing.current?.abort();
    };
  }, []);

  const uploaded = LAYERS.filter(({ key }) => layers[key] !== undefined);
  const canSave = LAYERS.every(({ key, optional }) => optional || layers[key] !== undefined);

  const save = async (
    contents: Partial<Record<LayerKey, unknown>>,
    unitsPerMetre: number | null,
  ): Promise<boolean> => {
    if (storeyProblem) {
      setError(String(t(storeyProblemKey(storeyProblem), { storey: storeyText.trim() })));
      return false;
    }

    setSaving(true);
    setError(undefined);

    const floorPlan = new FloorPlan();
    floorPlan.officeId = officeId;
    floorPlan.storey = storey;
    floorPlan.borderLayer = contents.borderLayer as GeoJson;
    floorPlan.floorLayer = contents.floorLayer as GeoJson;
    floorPlan.roomLayer = contents.roomLayer as GeoJson;
    floorPlan.workplacesLayer = contents.workplacesLayer as GeoJsonWorkplaces;
    floorPlan.labelLayer = contents.labelLayer as GeoJsonLabels | undefined;

    const result = await saveFloorPlanForOffice(floorPlan, keycloak, unitsPerMetre);

    setSaving(false);

    if (result === 'saved') {
      onCreated(storey);
      return true;
    }

    // A storey that already has a plan is worth its own words: the fix is to edit that plan or
    // pick another storey, not to try the same upload again.
    const failures: Record<string, string> = {
      duplicateStorey: 'floorplan-editor-create-duplicate-storey',
      invalidStorey: 'floorplan-storey-negative',
    };
    setError(String(t(failures[result] ?? 'floorplan-editor-create-failed')));
    return false;
  };

  /** One GeoJSON file into one layer. Nothing is sent until every required layer is present. */
  const readLayer = (layer: LayerKey) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    // Cleared so choosing the same file twice fires a change event the second time.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = (): void => {
      let parsed: unknown;

      // Parsed here rather than inside the state updater. React runs an updater during
      // rendering, so a throw from in there escapes this try entirely and takes the whole
      // panel down — which is what a mistyped file extension used to do.
      try {
        parsed = JSON.parse(String(reader.result));
      } catch {
        setError(String(t('floorplan-editor-import-not-json')));
        return;
      }

      setLayers((current) => ({ ...current, [layer]: parsed }));
      setError(undefined);
    };

    reader.onerror = (): void => setError(String(t('floorplan-editor-import-unreadable')));
    reader.readAsText(file);
  };

  /** The sample floor, saved as it is and then opened in the editor like any other plan. */
  const startFromSample = (): void => {
    const payload = serializeFloorPlan(sampleFloorPlan(officeId, storey, {
      openOffice: String(t('floorplan-sample-open-office')),
      meetingRoom: String(t('floorplan-sample-meeting-room')),
      focusRoom: String(t('floorplan-sample-focus-room')),
      kitchen: String(t('floorplan-sample-kitchen')),
      entrance: String(t('floorplan-sample-entrance')),
    }), 'full');

    save(
      {
        borderLayer: payload.borderLayer,
        floorLayer: payload.floorLayer,
        roomLayer: payload.roomLayer,
        workplacesLayer: payload.workplacesLayer,
        labelLayer: payload.labelLayer,
      },
      SAMPLE_UNITS_PER_METRE,
    ).catch(() => setError(String(t('floorplan-editor-create-failed'))));
  };

  const leaveReview = (): void => setReview(null);

  /**
   * Reads the image turned `turn` degrees clockwise, in the browser — the image never leaves it —
   * and shows the administrator what was found, to correct before any plan is made. A turn that
   * is not a quarter is the administrator's own straightening, so the tracer's is left off.
   */
  const readImage = async (file: Blob, turn: number): Promise<void> => {
    tracing.current?.abort();
    const controller = new AbortController();
    tracing.current = controller;

    setError(undefined);
    setReadingImage(true);
    setProgress(null);

    try {
      const read = await readPictureInBackground(file, {
        degrees: turn,
        signal: controller.signal,
        onProgress: (next): void => { if (tracing.current === controller && mounted.current) setProgress(next); },
        options: { straighten: turn % 90 === 0 },
      });
      // A newer reading has taken over, or the panel has gone: this one's answer is not wanted.
      if (tracing.current !== controller || !mounted.current) return;
      const { sketch } = read;

      if (!sketch.isFloorPlan) {
        setError(String(t('floorplan-editor-image-nothing-found')));
        return;
      }

      setReview({
        sketch,
        ink: read.ink,
        imageUrl: read.previewUrl,
        aspect: sketch.aspect ?? read.height / read.width,
        file,
        turn,
      });
    } catch (failure) {
      // Stopped on purpose: whatever stopped it is already showing what comes next.
      if (failure instanceof Error && failure.message === TRACE_CANCELLED) return;
      if (tracing.current !== controller || !mounted.current) return;
      setError(String(t(failure instanceof PictureUnreadable ? unreadableKeyOf(failure.format) : 'floorplan-editor-create-failed')));
    } finally {
      if (tracing.current === controller && mounted.current) {
        tracing.current = null;
        setReadingImage(false);
        setProgress(null);
      }
    }
  };

  /** First step with an image: read it as it is. Opening it is part of reading it, off the page. */
  const startFromImage = async (file: File): Promise<void> => {
    setError(undefined);
    await readImage(file, 0);
  };

  /** Second step: the corrected finds, made into a plan and opened in the editor. */
  const createFromReview = async (corrected: FloorPlanSketch): Promise<void> => {
    if (!review) {
      return;
    }

    // Marked, it is replaced by the editor's own; unmarked, it stays exactly as drawn.
    const asDrawn = review.ink ? await asDrawnInBackground(review.ink, corrected) : [];
    const plan = sketchToFloorPlan(corrected, review.aspect, officeId, storey, { buildingWidthMetres: buildingWidth, asDrawn });

    if (!plan) {
      setError(String(t('floorplan-editor-image-nothing-found')));
      return;
    }

    const payload = serializeFloorPlan(plan.floorPlan, 'full');
    const saved = await save(
      {
        borderLayer: payload.borderLayer,
        floorLayer: payload.floorLayer,
        roomLayer: payload.roomLayer,
        workplacesLayer: payload.workplacesLayer,
        labelLayer: payload.labelLayer,
      },
      IMAGE_PLAN_UNITS_PER_METRE,
    );

    if (saved) {
      leaveReview();
      const summary = String(t('floorplan-editor-image-created', {
        rooms: plan.rooms,
        desks: plan.desks,
        walls: plan.walls,
        doors: plan.doors,
        windows: plan.windows,
        objects: plan.objects,
        asDrawn: plan.asDrawn,
      }));
      notify(plan.scaleEstimated ? `${summary} ${String(t('floorplan-editor-image-scale-estimated'))}` : summary, true);
    }
  };

  const applyConversion = (conversion: DrawingConversion): void => {
    // A converted drawing carries all five layers at once, so it goes straight to the server
    // rather than waiting for the layer-by-layer form to be filled in as well.
    save(
      {
        borderLayer: conversion.borderLayer,
        floorLayer: conversion.floorLayer,
        roomLayer: conversion.roomLayer,
        workplacesLayer: conversion.workplacesLayer,
        labelLayer: conversion.labelLayer,
      },
      conversion.unitsPerMetre,
    ).catch(() => setError(String(t('floorplan-editor-create-failed'))));
  };

  if (review) {
    return (
      <Stack sx={{ overflowY: 'auto' }}>
        {error && <Alert severity="error" sx={{ mx: 2, mt: 2, py: 0 }}>{error}</Alert>}
        <ImageReview
          // A new reading is a new review: its finds, its selection and its history.
          key={`${review.turn}-${review.imageUrl.length}`}
          sketch={review.sketch}
          imageUrl={review.imageUrl}
          aspect={review.aspect}
          turn={review.turn}
          reading={readingImage}
          readingLabel={progress ? String(t(readingStepKeyOf(progress))) : undefined}
          onTurn={(turn): void => { readImage(review.file, turn).catch(() => undefined); }}
          saving={saving}
          onBack={leaveReview}
          onCreate={(corrected): void => {
            createFromReview(corrected).catch(() => setError(String(t('floorplan-editor-create-failed'))));
          }}
        />
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" sx={{ p: 3, overflowY: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          width: '100%',
          maxWidth: 640,
          border: `1px solid ${editorTheme.line}`,
          borderRadius: `${editorTheme.radius}px`,
          boxShadow: editorTheme.shadowPanel,
        }}
      >
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center">
              <Typography variant="h6" sx={{ color: editorTheme.ink, flexGrow: 1 }}>
                {t('floorplan-editor-create-title')}
              </Typography>
              {onCancel && (
                <Button size="small" onClick={onCancel} sx={{ textTransform: 'none' }}>{t('cancel')}</Button>
              )}
            </Stack>
            <Typography variant="body2" sx={{ color: editorTheme.inkDim }}>
              {t(EDITOR_FEATURES.drawingImport ? 'floorplan-editor-create-hint' : 'floorplan-editor-create-hint-geojson')}
            </Typography>
          </Stack>

          {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}

          <TextField
            size="small"
            type="number"
            label={String(t('floorplan-editor-create-storey'))}
            value={storeyText}
            onChange={(event): void => setStoreyText(event.target.value)}
            inputProps={{ min: 0, step: 1 }}
            sx={{ maxWidth: 260 }}
            error={storeyProblem !== undefined}
            helperText={String(storeyProblem
              ? t(storeyProblemKey(storeyProblem), { storey: storeyText.trim() })
              : t('floorplan-editor-create-storey-hint'))}
          />

          <Divider />

          <Typography sx={sectionHeadingSx}>{t('floorplan-editor-create-from-sample')}</Typography>
          <Stack spacing={1} alignItems="flex-start">
            <Typography variant="body2" sx={{ color: editorTheme.inkDim }}>
              {t('floorplan-editor-create-from-sample-hint')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<FloorPlanIcon size={16} />}
              disabled={saving || readingImage || storeyProblem !== undefined}
              onClick={startFromSample}
              sx={{ textTransform: 'none' }}
            >
              {t('floorplan-editor-create-from-sample-start')}
            </Button>
          </Stack>

          <Divider />

          <Typography sx={sectionHeadingSx}>{t('floorplan-editor-create-from-image')}</Typography>
          <Stack spacing={1} alignItems="flex-start">
            <Typography variant="body2" sx={{ color: editorTheme.inkDim }}>
              {t('floorplan-editor-create-from-image-hint')}
            </Typography>
            <TextField
              size="small"
              type="number"
              label={String(t('floorplan-editor-image-width'))}
              value={buildingWidthText}
              onChange={(event): void => setBuildingWidthText(event.target.value)}
              inputProps={{ min: 0, step: 0.1 }}
              sx={{ maxWidth: 260 }}
              error={buildingWidthInvalid}
              helperText={String(t(buildingWidthInvalid ? 'floorplan-editor-image-width-invalid' : 'floorplan-editor-image-width-hint'))}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                component="label"
                variant="outlined"
                startIcon={<ImageSearchOutlineIcon size={16} />}
                disabled={saving || readingImage || storeyProblem !== undefined || buildingWidthInvalid}
                sx={{ textTransform: 'none' }}
              >
                {t('floorplan-editor-create-from-image-choose')}
                <input
                  type="file"
                  accept={PICTURE_ACCEPT}
                  hidden
                  onChange={(event): void => {
                    const file = event.target.files?.[0];
                    // Cleared so the same image can be chosen again after a failed attempt.
                    // eslint-disable-next-line no-param-reassign
                    event.target.value = '';
                    if (file) {
                      startFromImage(file).catch(() => setError(String(t('floorplan-editor-create-failed'))));
                    }
                  }}
                />
              </Button>
              {readingImage && (
                <Stack spacing={0.5} sx={{ minWidth: 220, flex: 1 }}>
                  <Typography variant="caption" sx={{ color: editorTheme.inkDim }}>
                    {t(progress ? readingStepKeyOf(progress) : 'floorplan-editor-create-from-image-reading')}
                  </Typography>
                  <LinearProgress
                    variant={progress ? 'determinate' : 'indeterminate'}
                    value={progress ? progress.share * 100 : undefined}
                    aria-label={String(t('floorplan-editor-create-from-image-reading'))}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>

          <Divider />

          {EDITOR_FEATURES.drawingImport && (
            <>
              <Typography sx={sectionHeadingSx}>{t('floorplan-editor-create-from-cad')}</Typography>
              <DrawingImport officeId={officeId} onConverted={applyConversion} />

              <Divider />
            </>
          )}

          <Typography sx={sectionHeadingSx}>{t('floorplan-editor-create-from-geojson')}</Typography>

          <Stack spacing={1}>
            {LAYERS.map(({ key, labelKey, optional }) => (
              <Stack key={key} direction="row" spacing={1} alignItems="center">
                <Button
                  component="label"
                  size="small"
                  variant={layers[key] === undefined ? 'outlined' : 'contained'}
                  startIcon={<FileUploadOutlineIcon size={14} />}
                  disabled={saving}
                  sx={{ textTransform: 'none', minWidth: 190, justifyContent: 'flex-start' }}
                >
                  {t(labelKey)}
                  {optional ? ` (${String(t('floorplan-editor-create-optional'))})` : ''}
                  <input type="file" accept=".json,.geojson,application/json" hidden onChange={readLayer(key)} />
                </Button>
                {layers[key] !== undefined && (
                  <Typography variant="caption" sx={{ color: editorTheme.ok }}>
                    {t('floorplan-editor-create-layer-ready')}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              disabled={!canSave || saving || storeyProblem !== undefined}
              onClick={(): void => {
                save(layers, null).catch(() => setError(String(t('floorplan-editor-create-failed'))));
              }}
            >
              {t('floorplan-editor-create-save')}
            </Button>
            {saving && <CircularProgress size={18} />}
            <Typography variant="caption" sx={{ color: editorTheme.inkDim }}>
              {t('floorplan-editor-create-progress', { done: uploaded.length, total: LAYERS.length })}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default FloorPlanStarter;
